/**
 * Content Data - Projects and Passions
 *
 * PROJECTS
 * Each project can have the following properties:
 * - id: unique identifier (required)
 * - name: project name (required)
 * - color: background color (required)
 * - boxShadowColor: glow color for borders and effects (optional, defaults to cyan)
 * - description: short description shown on card (required) (between 25 and 30 words)
 * - githubLink: GitHub repository URL (optional)
 * - link: live website URL (optional)
 * - linksNote: text note for middle section (e.g., "unpublished") - shows instead of empty space (optional)
 * - clip: set to true to use video clip (.mp4) instead of static image (.png) (optional)
 * - video: set to true to show video icon instead of website icon - opens video modal (optional)
 * - poster: set to true to enable high-res poster image modal with zoom/scroll (optional)
 *
 * Display Rules:
 * - Image: Shows video clip if clip=true, otherwise PNG
 * - Left icon: GitHub icon (if githubLink exists) or placeholder
 * - Middle: linksNote text (if exists) or placeholder
 * - Right icon: Video button (if video=true) OR Website icon (if link exists) OR Poster button (if poster=true) or placeholder
 *
 * Card Click Priority (if no specific link/button clicked):
 * 1. link (website) - opens in new tab
 * 2. video - opens video modal
 * 3. poster - opens zoomable poster modal
 * 4. githubLink - opens in new tab
 *
 * File paths:
 * - Images: ./images/project-images/project-image-{id}.png
 * - Video clips (preview): ./videos/project-clips/project-clip-{id}.mp4
 * - Videos (full demo): ./videos/project-videos/project-video-{id}.mp4
 * - Posters (high-res): ./images/project-posters/project-poster-{id}.png
 *
 * PASSIONS
 * Passion descriptions are organized by id (coding, data, ml)
 * - title: Display title for the passion
 * - description: HTML string with detailed description (supports <p>, <strong>, <br> tags)
 */

const projects = [
  {
    id: 13,
    name: "Delve",
    color: "#0A0A0A",
    clip: false,
    boxShadowColor: "#C89A4A",
    description: "A fun little browser dungeon-crawler. Sword, shield, bow and arrow and lots of little pixel creatures.",
    // private repo — no GitHub link, just the live game
    link: "https://pascalpan.com/creations/Delve/"
  },
  {
    id: 11,
    name: "Obsidian Agent",
    color: "#0A0A0A",
    clip: false,
    boxShadowColor: "#A927FF", 
    description: "An Obsidian plugin that adds an autonomous AI assistant to your vault. Explores notes, proposes reviewable edits, and manages vault structure through a ReAct agent with 35 integrated tools.",
    // OLD: "A custom JavaScript chess engine with a compact board interface. Generates puzzles and starting positions in real time based on user preferences and configurable rule constraints."
    githubLink: "https://github.com/PascalJPan/obsidian_ai_agent",
  },
  {
    id: 3,
    name: "Tiny Chess",
    color: "#0A0A0A", // OLD: #000000
    clip: true,
    boxShadowColor: "#E685B5", // OLD: #D37BA2
    description: "A JavaScript chess engine built from scratch with move validation, position evaluation, and dynamic puzzle generation. Configurable constraints let users customize starting positions and puzzle generation.",
    // OLD: "A custom JavaScript chess engine with a compact board interface. Generates puzzles and starting positions in real time based on user preferences and configurable rule constraints."
    githubLink: "https://github.com/PascalJPan/TinyChess",
    link: "https://pascalpan.com/creations/TinyChess/"
  },
    {
    id: 6,
    name: "Dungeon Desk",
    color: "#0A0A0A", // OLD: #121212
    boxShadowColor: "#FF6666", // OLD: #FF474B
    clip: true,
    description: "A campaign management platform for tabletop RPG game masters. Features hierarchical world-building, combat tracking, and AI-powered entity extraction from text within a React and TypeScript frontend.",
    // OLD: "A campaign management tool for tabletop RPG game masters. Supports structured world-building, combat tracking, and AI-assisted entity extraction using a modular React and TypeScript architecture."
    githubLink: "https://github.com/PascalJPan/dungeondesk",
    link: "https://pascalpan.com/creations/DungeonDesk/"
  },
  {
    id: 12,
    name: "Re:be",
    color: "#0A0A0A", // OLD: #000000
    clip: false,
    boxShadowColor: "#ffffff", // OLD: #D37BA2
    description: "An image-to-audio social platform where users upload photos, draw squiggle gestures, and pick colors to generate unique music through an OpenAI vision and ElevenLabs audio pipeline.",
    // OLD: "A custom JavaScript chess engine with a compact board interface. Generates puzzles and starting positions in real time based on user preferences and configurable rule constraints."
    githubLink: "https://github.com/PascalJPan/re_be",
    link: "https://pascalpan.com/creations/re_be/"
  },
    {
    id: 8,
    name: "Perturb Shiny",
    color: "#0A0A0A", // OLD: #000000
    video: true,
    boxShadowColor: "#4DB8B8", // OLD: #3AA6A6
    description: "An interactive R Shiny dashboard for analyzing single-cell perturbation screens. Features modular components for transcription factor analysis, gating workflows, and customizable plots to help researchers explore gene regulatory relationships.",
    // OLD: "A modular Shiny application for exploring perturbation-based single-cell analyses. Provides TF-centric views, gated results, and interactive plots to make complex regulatory analyses accessible to lab members and collaborators."
    githubLink: "https://github.com/PascalJPan/PerturbSeqShiny"
  },
  {
    id: 5,
    name: "Gene Networks",
    color: "#0A0A0A", // OLD: #000000
    poster: true,
    boxShadowColor: "#66D9D9", // OLD: #3AA6A6
    description: "Computational methods for measuring gene regulatory network conservation across species. Developed and benchmarked 40+ statistical metrics on single-cell data and evaluated their robustness to biological and power effects.",
    // OLD: "Statistical pipelines for quantifying gene regulatory network conservation across species. Developed and benchmarked over 40 metrics, with a focus on robustness, bias correction, and power effects in single-cell data."
    githubLink: "",
    linksNote: "(unpublished)"
  },
    {
    id: 7,
    name: "Walnut",
    color: "#0A0A0A", // OLD: #1A0F0A
    boxShadowColor: "#FFB84D", // OLD: #FFAC26
    clip: true,
    description: "A smart health diary that converts conversational voice or text into structured medical summaries. Extracts symptoms, medications, and timelines to make patient histories easier to review and share with doctors.",
    // OLD: "A prototype smart health diary for tracking symptoms and medical history. Transforms voice or text entries into structured, doctor-ready summaries to reduce friction between patients and healthcare providers."
    githubLink: "https://github.com/jonasdieker/Walnut",
    link: ""
  },
  {
    id: 10,
    name: "Todo Quests",
    color: "#0A0A0A", // NEW: was missing
    boxShadowColor: "#33B89E", // OLD: #008175
    clip: true,
    description: "A Flutter Android app that transforms Todoist tasks into an XP-based progression system. Includes a live home screen widget, customizable experience formulas, and background sync to gamify habits.",
    // OLD: "A Flutter-based Android app that gamifies task completion by converting Todoist tasks into experience points. Features a real-time home screen widget, customizable XP formulas, and background synchronization."
    githubLink: "https://github.com/PascalJPan/TodoQuests",
    link: ""
  },
  {
    id: 4,
    name: "Origami",
    color: "#0A0A0A", // OLD: #0D1015
    boxShadowColor: "#FFD966", // OLD: #F3C24C
    clip: true,
    description: "A convolutional neural network for predicting protein secondary structure from amino acid sequences. Combines PyTorch-based modeling with a React web interface to serve real-time predictions.",
    // OLD: "A convolutional neural network for predicting protein secondary structure from amino acid sequences. Built collaboratively at an AI hackathon, combining PyTorch modeling with a lightweight web interface."
    githubLink: "https://github.com/PascalJPan/Origami",
    link: "https://pascalpan.com/creations/origami/"
  },
  {
    id: 1,
    name: "Phyto Evolve",
    color: "#0A0A0A", // OLD: #000000
    boxShadowColor: "#B399FF", // OLD: #CA83FF
    clip: true,
    description: "An interactive simulation of microalgae evolution using genetic algorithms. Organisms adapt their morphology and visual traits in response to environmental pressures, creating emergent patterns through selection and mutation.",
    // OLD: "A web-based sci-art simulation exploring microalgae diversity with evolutionary algorithms. Organisms gradually adapt shape and looks based on environmental pressure and evolutionary selection rules."
    githubLink: "https://github.com/PascalJPan/PhytoEvolve",
    link: "https://pascalpan.com/creations/PhytoEvolve/"
  }
];


export const passions = {
  coding: {
    title: "Software Engineering",
    description: `<p>
                  I build interactive software systems that turn concepts into usable tools.
                  My focus is on clear structure and predictable behavior as systems grow in scope and complexity.
                  </p>
                  <p>
                  <br>
                  </p>
                  <p>
                  Across JavaScript and Python backends with React and R Shiny frontends, I design modular architectures with explicit data flow that remain understandable beyond the first implementation.
                  </p>`
                  /* OLD:
    description: `<p>
                  I enjoy building interactive software systems that combine clean logic, thoughtful design, and exploration.
                  My projects focus on turning complex ideas into tools that invite users to play, experiment, and understand.
                  </p>
                  <p>
                  <br>
                  </p>
                  <p>
                  I care about software architecture and maintainability, designing modular systems that scale from prototypes to real tools.
                  Using JavaScript, React, and Shiny, I build interfaces where structure, interaction, and feedback work together.
                  </p>`
    */
  },
  data: {
    title: "Data Science",
    description: `<p>
                  My bioinformatics background trained me to work with noisy, high-dimensional data.
                  I focus on data quality, validating results, and separating real signals from technical artifacts.
                  </p>

                  <p> <br></p>

                  <p>
                  Using Python and R, I build reproducible analyses and visualizations for comparison and benchmarking.
                  I value clear communication, especially when results inform decisions beyond the code itself.
                  </p>`
    /* OLD:
    description: `<p>
                  My background in bioinformatics trained me to work with noisy, high-dimensional data.
                  I focus on exploratory analysis, benchmarking, and careful validation to understand what results actually mean.
                  </p>

                  <p> <br></p>

                  <p>
                  Using Python and R, I build reproducible pipelines and visualizations that surface patterns, limitations, and assumptions.
                  Clear communication is central to my work, whether for scientific audiences or cross-disciplinary teams.
                  </p>`
    */
  },
  ml: {
    title: "Machine Learning",
    description: `<p>
                  My master’s thesis focused on statistical modeling and simulations to analyze behavior in complex biological data.
                  I approach ML with an emphasis on assumptions, evaluation, and integration into larger systems.
                  </p>
                  <p><br></p>

                  <p>
                  I use modern ML-assisted tools like Claude and LLM APIs to prototype efficiently and test modeling ideas.
                  At the same time, I keep core logic transparent and maintain control over the code, using these tools to deepen understanding rather than replace it.
                  </p>
                  `
    /* OLD:
    description: `<p>
                  I use machine learning as a practical tool rather than a black box.
                  In my master's thesis, I applied statistical modeling and simulations to study bias, robustness, and power in single-cell data.
                  </p>
                  <p> <br></p>
                  <p>
                  Beyond academic work, I build applied ML systems such as neural networks for protein structure prediction.
                  I am especially interested in integrating models into usable software with clear assumptions and constraints.
                  </p>`
    */
  }
};




export default projects;
